import { useEffect } from 'react';
import * as THREE from 'three';

export default function useSkyline(canvasRef) {
  useEffect(() => {
    const host = canvasRef.current;
    if (!host) return undefined;

    let renderer = null;
    let onMove = null;
    let resize = null;
    let raf = 0;

    const disposables = {
      geometries: [],
      materials: [],
      textures: [],
    };

    const track = (obj) => {
      if (!obj) return obj;
      if (obj.isBufferGeometry || obj.isGeometry) disposables.geometries.push(obj);
      if (obj.isMaterial) disposables.materials.push(obj);
      if (obj.isTexture) disposables.textures.push(obj);
      return obj;
    };

    try {
      const w = host.clientWidth || window.innerWidth;
      const h = host.clientHeight || window.innerHeight;
      
      if (w <= 0 || h <= 0) return () => {};

      const ink = 0x0a0a0b;
      const gold = 0xc9a86a;

      const scene = new THREE.Scene();
      scene.fog = new THREE.FogExp2(ink, 0.0042);

      const camera = new THREE.PerspectiveCamera(58, w / h, 0.1, 2000);

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
      renderer.setSize(w, h);
      renderer.setClearColor(ink, 1);
      renderer.domElement.style.display = 'block';
      host.appendChild(renderer.domElement);

      const fillMat = track(new THREE.MeshBasicMaterial({ color: 0x0b0c0f }));
      const edgeMat = track(new THREE.LineBasicMaterial({ color: gold, transparent: true, opacity: 0.4 }));
      const spireMat = track(new THREE.LineBasicMaterial({ color: 0xe8c47e, transparent: true, opacity: 0.95, linewidth: 2 }));
      
      const city = new THREE.Group();
      const R_FOOT = 105;
      
      const winPositions = [];
      
      let seed = 1337;
      const random = () => {
        seed = (seed * 16807) % 2147483647;
        return (seed - 1) / 2147483646;
      };

      for (let x = -100; x <= 100; x += 11) {
        for (let z = -100; z <= 100; z += 11) {
          const d = Math.sqrt(x * x + z * z);
          if (d > R_FOOT) continue;
          
          const isCore = Math.abs(x) < 15 && Math.abs(z) < 15;
          const falloff = Math.max(0, 1 - d / R_FOOT);
          const bw = 4.5 + random() * 4;
          const bd = 4.5 + random() * 4;
          
          let bh = 5 + Math.pow(falloff, 1.5) * (40 + random() * 70) + random() * 6;
          if (isCore && random() > 0.8) {
            bh += 60 + random() * 40;
          }

          const geo = track(new THREE.BoxGeometry(bw, bh, bd));
          const px = x + (random() * 4 - 2);
          const pz = z + (random() * 4 - 2);
          
          const fill = new THREE.Mesh(geo, fillMat);
          fill.position.set(px, bh / 2, pz);
          city.add(fill);
          
          const useSpire = isCore && bh > 110;
          const edges = new THREE.LineSegments(
            track(new THREE.EdgesGeometry(geo)), 
            useSpire ? spireMat : edgeMat
          );
          edges.position.copy(fill.position);
          city.add(edges);
          
          if (bh > 20 && random() > 0.3) {
            const numWins = Math.floor(random() * 10) + 2;
            for(let i = 0; i < numWins; i++) {
              const wy = random() * bh;
              const wx = px + (random() - 0.5) * bw;
              const wz = pz + (random() - 0.5) * bd;
              winPositions.push(wx, wy, wz);
            }
          }
        }
      }
      scene.add(city);
      
      const winGeo = track(new THREE.BufferGeometry());
      winGeo.setAttribute('position', new THREE.Float32BufferAttribute(winPositions, 3));
      const winMat = track(new THREE.PointsMaterial({
        color: 0xffeebb,
        size: 1.5,
        transparent: true,
        opacity: 0.8,
        sizeAttenuation: true
      }));
      const windows = new THREE.Points(winGeo, winMat);
      scene.add(windows);
      
      const starPositions = [];
      for(let i=0; i<300; i++) {
        starPositions.push(
          (random() - 0.5) * 1000,
          300 + random() * 500,
          (random() - 0.5) * 1000
        );
      }
      const starGeo = track(new THREE.BufferGeometry());
      starGeo.setAttribute('position', new THREE.Float32BufferAttribute(starPositions, 3));
      const starMat = track(new THREE.PointsMaterial({
        color: 0xffffff,
        size: 1.2,
        transparent: true,
        opacity: 0.6
      }));
      const stars = new THREE.Points(starGeo, starMat);
      scene.add(stars);
      
      const searchlights = new THREE.Group();
      const coneGeo = track(new THREE.ConeGeometry(8, 400, 16, 1, true));
      coneGeo.translate(0, 200, 0); 
      const coneMat = track(new THREE.MeshBasicMaterial({
        color: 0xc9a86a,
        transparent: true,
        opacity: 0.05,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide
      }));
      for(let i=0; i<2; i++) {
        const cone = new THREE.Mesh(coneGeo, coneMat);
        cone.position.set((i===0?1:-1)*10, 0, 0);
        cone.rotation.z = (random() - 0.5);
        cone.rotation.x = (random() - 0.5);
        searchlights.add(cone);
      }
      scene.add(searchlights);

      const grid = new THREE.GridHelper(420, 42, gold, gold);
      track(grid.material); 
      track(grid.geometry);
      grid.material.opacity = 0.06;
      grid.material.transparent = true;
      scene.add(grid);

      const mouse = { x: 0, y: 0 };
      let elapsed = 0;
      const clock = new THREE.Clock();

      onMove = (e) => {
        mouse.x = (e.clientX / window.innerWidth) - 0.5;
        mouse.y = (e.clientY / window.innerHeight) - 0.5;
      };

      const renderFrame = () => {
        const mx = mouse.x;
        const my = mouse.y;
        
        searchlights.children.forEach((c, i) => {
          c.rotation.y = elapsed * (0.2 + i*0.1);
          c.rotation.z = Math.sin(elapsed * (0.5 + i*0.2)) * 0.3;
        });

        winMat.opacity = 0.6 + Math.sin(elapsed * 2) * 0.2;

        const angle = elapsed * 0.085 + mx * 0.5;
        const radius = 188 + Math.sin(elapsed * 0.12) * 14;
        
        const verticalDrift = Math.sin(elapsed * 0.25) * 12;
        const height = 60 + Math.sin(elapsed * 0.21) * 16 - my * 18 + verticalDrift;
        
        camera.position.set(Math.cos(angle) * radius, height, Math.sin(angle) * radius);
        camera.lookAt(0, 30, 0);
        renderer.render(scene, camera);
      };

      resize = () => {
        const rw = host.clientWidth || window.innerWidth;
        const rh = host.clientHeight || window.innerHeight;
        camera.aspect = rw / rh;
        camera.updateProjectionMatrix();
        renderer.setSize(rw, rh);
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          renderFrame();
        }
      };

      const isReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (isReduced) {
        renderFrame();
      } else {
        window.addEventListener('pointermove', onMove);
        const animate = () => {
          raf = requestAnimationFrame(animate);
          const dt = Math.min(clock.getDelta(), 0.05);
          elapsed += dt;
          renderFrame();
        };
        animate();
      }

      window.addEventListener('resize', resize);

      return () => {
        if (raf) cancelAnimationFrame(raf);
        if (onMove) window.removeEventListener('pointermove', onMove);
        if (resize) window.removeEventListener('resize', resize);
        
        disposables.geometries.forEach(g => g.dispose());
        disposables.materials.forEach(m => m.dispose());
        disposables.textures.forEach(t => t.dispose());

        if (renderer) {
          renderer.dispose();
          const el = renderer.domElement;
          if (el && el.parentNode) el.parentNode.removeChild(el);
        }
      };
    } catch (err) {
      console.warn('WebGL setup failed, falling back:', err);
      if (raf) cancelAnimationFrame(raf);
      if (onMove) window.removeEventListener('pointermove', onMove);
      if (resize) window.removeEventListener('resize', resize);
      if (renderer) {
        try {
          renderer.dispose();
          const el = renderer.domElement;
          if (el && el.parentNode) el.parentNode.removeChild(el);
        } catch (e) {
          // ignore
        }
      }
      return () => {};
    }
  }, [canvasRef]);
}